/**
 * useDiscardGuard - "Discard changes?" in front of closing a modal form.
 *
 * The edit modals close on Escape, on a click outside the panel, and on
 * their X — all easy to hit while typing. With `isDirty` true, `requestClose`
 * asks first and closes only on Discard; otherwise it closes at once.
 *
 *	 const { requestClose, discardPrompt } = useDiscardGuard({ isDirty, onClose: closeEditModal });
 *	 <Modal isOpen={open} onClose={requestClose}>…</Modal>
 *	 {discardPrompt}
 *
 * Same shape as useNotifyPrompt: the hook owns the dialog and hands back the
 * element to render. Leaving the PAGE with the modal open is the form's own
 * UnsavedChangesPrompt's job; this only guards closing the modal.
 */
import React, { useCallback, useState } from 'react';
import { ConfirmModal } from '../components/organisms/ConfirmModal';

export interface UseDiscardGuardOptions {
	/** True while the modal's form has changes that would be lost. */
	isDirty: boolean;
	/** Actually closes the modal. Called at once when clean, after Discard when not. */
	onClose: () => void;
}

export interface UseDiscardGuard {
	/** Wire this to the modal's onClose. */
	requestClose: () => void;
	/** Render this next to the modal. */
	discardPrompt: React.ReactNode;
}

export function useDiscardGuard({ isDirty, onClose }: UseDiscardGuardOptions): UseDiscardGuard {
	const [asking, setAsking] = useState(false);

	const requestClose = useCallback(() => {
		if (isDirty) {
			setAsking(true);
		} else {
			onClose();
		}
	}, [isDirty, onClose]);

	const discardPrompt = (
		<ConfirmModal
			isOpen={asking}
			onClose={() => setAsking(false)}
			onConfirm={() => {
				setAsking(false);
				onClose();
			}}
			title="Discard changes?"
			message="You have unsaved changes. If you close this now, they will be lost."
			confirmLabel="Discard"
			cancelLabel="Keep editing"
			variant="danger"
			dataRef="organism-discard-changes-prompt"
		/>
	);

	return { requestClose, discardPrompt };
}
